/**
 * Ensures hex images load ring-by-ring: all hexes in ring R can load in parallel
 * (up to MAX_PARALLEL in hexImageLoader) only after the whole ring R-1 has completed.
 * When the grid changes (pan/zoom), we reset so the new grid starts from ring 0.
 */

let currentGridId = 0;
/** Ring index up to which all hexes have completed. -1 = none, 0 = ring 0 done, etc. */
let completedRingsUpTo = -1;
/** For each ring, how many hexes have reported complete. */
const ringCompleteCount: number[] = [];
/** For each ring, how many hexes are in this ring (set on reset). */
let ringSizes: number[] = [];

type Waiter = {
  ringIndex: number;
  gridId: number;
  resolve: () => void;
  reject: (err: unknown) => void;
};
const waiters: Waiter[] = [];

export const GRID_CHANGED_MSG = "grid changed";

/**
 * Call when the grid changes. ringSizes[r] = number of hexes in ring r for this grid.
 * Ring 0 = center (1 hex), ring 1 = next 6, ring 2 = next 12, etc.
 */
export function resetLoadOrder(newGridId: number, newRingSizes: number[]): void {
  currentGridId = newGridId;
  completedRingsUpTo = -1;
  ringSizes = newRingSizes;
  ringCompleteCount.length = 0;
  for (let r = 0; r < newRingSizes.length; r++) {
    ringCompleteCount[r] = 0;
  }
  const err = new Error(GRID_CHANGED_MSG);
  for (const w of waiters) {
    w.reject(err);
  }
  waiters.length = 0;
}

/** Resolves when this ring is allowed to load (previous ring has fully completed). Rejects if grid changed. */
export function requestLoadOrder(ringIndex: number, gridId: number): Promise<void> {
  if (gridId !== currentGridId) {
    return Promise.reject(new Error(GRID_CHANGED_MSG));
  }
  if (completedRingsUpTo >= ringIndex - 1) {
    return Promise.resolve();
  }
  return new Promise<void>((resolve, reject) => {
    waiters.push({ ringIndex, gridId, resolve, reject });
  });
}

/** Call when one hex in this ring has finished loading. When the whole ring is done, the next ring is released. */
export function reportLoadComplete(ringIndex: number, gridId: number): void {
  if (gridId !== currentGridId) return;
  if (ringIndex >= ringSizes.length) return;
  ringCompleteCount[ringIndex] = (ringCompleteCount[ringIndex] || 0) + 1;
  const need = ringSizes[ringIndex];
  if (ringCompleteCount[ringIndex] >= need) {
    completedRingsUpTo = ringIndex;
    const nextRing = ringIndex + 1;
    for (let i = waiters.length - 1; i >= 0; i--) {
      const w = waiters[i];
      if (w.ringIndex === nextRing && w.gridId === currentGridId) {
        w.resolve();
        waiters.splice(i, 1);
      }
    }
  }
}
